import {create} from 'zustand/react';
import type {IGridLayout, IPollingData, IStreamingState, StreamingInfo} from '../pages/stream/types';
import callApi from '../apis/callApi.ts';
import {convertTimestampToHumanReadableDate} from '../utils/DateUtil.ts';
import {getCellLayout, getValidTokenFromStorage} from '../utils/StreamingUtil.ts';
import {jwtDecode} from "jwt-decode";

export interface IStreamingStore {
    // Polling data
    isHlsServerOn: boolean;
    setIsHlsServerOn: (val: boolean) => void;
    scheduledData: Array<StreamingInfo>;
    setScheduledData: (val: Array<StreamingInfo>) => void;
    liveUrl: string;
    setLiveUrl: (val: string) => void;
    streamingPollingData: IPollingData | null;
    setStreamingPollingData: (val: IPollingData) => void;

    // JWT token management
    setJitsiToken: (centerId: string) => Promise<string>;

    // Streaming state
    state: IStreamingState;
    setStreamingState: (next: Partial<IStreamingState>) => void;

    // Additional state management
    isConnected: boolean;
    setIsConnected: (connected: boolean) => void;

    connectionError: string | null;
    setConnectionError: (error: string | null) => void;

    // Track management state
    localTracksReady: boolean;
    setLocalTracksReady: (ready: boolean) => void;

    // Room info
    participantCount: number;
    setParticipantCount: (count: number) => void;

    // Media controls
    isMicMuted: boolean;
    setIsMicMuted: (muted: boolean) => void;

    isCameraEnabled: boolean;
    setIsCameraEnabled: (enabled: boolean) => void;

    // Reset function
    resetStreamingState: () => void;

    // grid-layout
    gridLayout: IGridLayout;
    setGridLayout: (val: IGridLayout) => void;

    // splash screen
    isSplashOn: boolean;
    setIsSplash: (val: boolean) => void;
    splashMessage: string;
    setSplashMessage: (val: string) => void;
}

const initialState: IStreamingState = {
    appId: '',
    room: '',
    jwt: '',
    connection: undefined,
    conference: undefined,
};

const useStreamingStore = create<IStreamingStore>((set, get) => ({
    isHlsServerOn: false,
    setIsHlsServerOn: (val: boolean) => {
        set({isHlsServerOn: val});
    },
    scheduledData: [],
    setScheduledData: (val: Array<StreamingInfo>) => {
        const prev = get().scheduledData;
        if (JSON.stringify(prev) !== JSON.stringify(val)) {
            set({scheduledData: val});
        }
    },
    liveUrl: '',
    setLiveUrl: (val: string) => {
        set({liveUrl: val});
    },
    streamingPollingData: null,
    setStreamingPollingData: (val: IPollingData) => {
        set({streamingPollingData: val});
    },
    setJitsiToken: async (val: string) => {
        const existingToken = localStorage.getItem('jitsi_token');
        let shouldFetchNewToken = false;
        if (existingToken) {
            try {
                const decoded = jwtDecode(existingToken);
                const expTime = (decoded.exp ?? 0) * 1000;
                console.log('토큰 만료일: ', convertTimestampToHumanReadableDate(expTime));
                const timeLeft = expTime - Date.now();
                if (timeLeft > 1000) {
                    console.log('기존 토큰 사용, 만료까지 남은 시간:', timeLeft, 'ms');
                    return existingToken;
                } else {
                    console.log('토큰 만료 임박:', timeLeft, 'ms → 새로 발급받음');
                    shouldFetchNewToken = true;
                }
            } catch (error) {
                console.warn('토큰 디코딩 실패 → 새로 발급받음:', error);
                shouldFetchNewToken = true;
            }
        } else {
            console.log('새로 발급받음');
            shouldFetchNewToken = true;
        }
        if (shouldFetchNewToken) {
            try {
                const {data} = await callApi().post(`jitsi/token/`, {
                    user: val,
                });
                const newToken = data.token.slice(2, -1);
                localStorage.setItem('jitsi_token', newToken);
                console.log('최종 토큰!', newToken);
                // 토큰의 context.user.name은 발급 요청에 보낸 user 값(= 경로당 코드)이 그대로
                // 되돌아온 것이다. 표시용 이름으로 쓰면 화면에 'ICS-000'이 뜬다 — 읽지 않는다.
                set((prev) => ({
                    state: {
                        ...prev.state,
                        appId: import.meta.env.VITE_ICSC_APP_ID,
                        room: import.meta.env.VITE_ROOM_NAME,
                        jwt: newToken,
                    },
                }));
                return newToken;
            } catch (error) {
                console.error('Jitsi Token fetching failed:', error);
                return ''; // 오류 발생 시 빈 문자열 반환 또는 적절한 에러 처리
            }
        }
    },
    state: {
        appId: import.meta.env.VITE_ICSC_APP_ID,
        room: import.meta.env.VITE_ROOM_NAME,
        jwt: getValidTokenFromStorage(),
        connection: undefined,
        conference: undefined,
    },
    setStreamingState: (next) => {
        set((prev) => ({
            state: {
                ...prev.state,
                ...next,
            },
        }));
    },
    isConnected: false,
    setIsConnected: (val: boolean) => {
        set({isConnected: val});
    },
    connectionError: null,
    setConnectionError: (val: string | null) => {
        set({connectionError: val});
    },
    localTracksReady: false,
    setLocalTracksReady: (val: boolean) => {
        set({localTracksReady: val});
    },
    participantCount: 0,
    setParticipantCount: (val: number) => {
        set({participantCount: val});
    },
    isMicMuted: true,
    setIsMicMuted: (val: boolean) => {
        set({isMicMuted: val});
    },
    isCameraEnabled: false,
    setIsCameraEnabled: (val: boolean) => {
        set({isCameraEnabled: val});
    },
    resetStreamingState: () => {
        set(
            {
                state: initialState,
                isConnected: false,
                connectionError: null,
                localTracksReady: false,
                participantCount: 0,
                isMicMuted: true,
                isCameraEnabled: true,
                streamingPollingData: null,
            },
            false
        );
    },
    gridLayout: {mode: 'new-32', sub: true},
    setGridLayout: (val: IGridLayout) => {
        // 레이아웃 변경 전에 Jitsi lastN 제약을 선제 업데이트하여
        // RemoteCell reattach 시점에 충분한 트랙이 수신되도록 함
        const conference = get().state.conference;
        if (conference) {
            const gridInfo = getCellLayout(val.mode);
            // remoteSize > 50인 대규모 그리드는 lastN=-1로 JVB 포워딩 제한 회피
            if (gridInfo.remoteSize > 50) {
                conference.setReceiverConstraints({
                    lastN: -1,
                    defaultConstraints: {maxHeight: 90},
                });
            } else {
                const multiplier = gridInfo.remoteSize > 30 ? 1.5 : 2.5;
                const maxHeight = gridInfo.remoteSize > 30 ? 120 : 180;
                const lastN = Math.max(1, Math.ceil(gridInfo.remoteSize * multiplier));
                conference.setReceiverConstraints({
                    lastN,
                    defaultConstraints: {maxHeight},
                });
            }
        }
        set({gridLayout: val});
    },
    isSplashOn: false,
    setIsSplash: (val: boolean) => {
        set({isSplashOn: val});
    },
    splashMessage: '방송 편성표를 불러오는 중...',
    setSplashMessage: (val: string) => {
        set({splashMessage: val});
    },
}));

export default useStreamingStore;
